import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { useFpCampaignProductsService } from '@cart-services/hooks/useFpCampaignProductsService'
import { DialogValidateStock } from '@cart-ui/DialogValidateStock/DialogValidateStock'
import { FORTUNE_GROUP_BUYING_AUTH_ERROR, ProjectCode, THIRD_PARTY_ORDER_ERROR_CODES, type CountryCode } from '@constants'
import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { MemberLogins } from '@hooks-api/types/dataModel/fortuneCampaign'
import { ThemeProvider } from '@pets-ui/ThemeProvider/ThemeProvider'
import { colorTheme as aquaColorTheme } from '@pets-ui/themes/aqua'
import { colorTheme as dcsColorTheme } from '@pets-ui/themes/dcs'
import { colorTheme as fastpassColorTheme } from '@pets-ui/themes/fastpass'
import { colorTheme as hmColorTheme } from '@pets-ui/themes/hm'
import { colorTheme as lmColorTheme } from '@pets-ui/themes/lm'
import { colorTheme as mnpColorTheme } from '@pets-ui/themes/mnp'
import { colorTheme as pmColorTheme } from '@pets-ui/themes/pm'
import { colorTheme as ppColorTheme } from '@pets-ui/themes/pp'
import { colorTheme as ytColorTheme } from '@pets-ui/themes/yt'
import { type Colors } from '@pets-ui/tokens/colors.stylex'
import { type Theme } from '@stylexjs/stylex'
import { Suspense, useEffect } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom-v5'
import { AppBarProvider } from '../../components/AppBar/AppBarProvider'
import { LoaderBuygather } from '../../components/LoaderFullPage/LoaderBuygather'
import { NotFound } from '../../components/NotFound'
import { PopupWrapper } from '../../components/PopupWrapper'
import { TheCampaignHeader } from '../../components/TheCampaignHeader'
import { GUIDED_LOGIN_KEY, storageGuidedLogin } from '../../hooks/apis/storage-guided-login'
import useLanguage, { type LocaleMapKey } from '../../hooks/use-language'
import { CampaignStatus } from '../../model/campaign'
import { Logout } from '../Logout'
import { RefreshCosignToken } from '../merchant/MerchantLogin/RefreshCosignToken'
import { CampaignAuthProcessing } from './CampaignAuthProcessing'
import { CampaignAuthProcessingWithWordPress } from './CampaignAuthProcessingWithWordPress'
import { CampaignLogin } from './CampaignLogin'
import { CampaignOrderList } from './CampaignOrder/CampaignOrderList'
import { Cart } from './cart/Cart'
import { CampaignCartProvider } from './cart/components/CartProvider/CampaignCartProvider'
import { ClientCheckout } from './client-checkout'
import ClientClosed from './client-closed'
import ClientContact from './client-contact'
import ClientFAQ from './client-faq'
import { ClientPayments } from './client-payments'
import ClientPrivacyPolicy from './client-privacy-policy'
import ClientThankyou from './client-thankyou'
import { OrderDetailContainer } from './OrderDetailContainer'
import { ProductCatalogProvider } from './product-catalog/components/ProductCatalogProvider/ProductCatalogProvider'
import { ProductCatalog } from './product-catalog/ProductCatalog'

const colorThemeMap: Partial<Record<ProjectCode, Theme<Colors>>> = {
  [ProjectCode.DCS]: dcsColorTheme,
  [ProjectCode.HM]: hmColorTheme,
  [ProjectCode.LM]: lmColorTheme,
  [ProjectCode.MNP]: mnpColorTheme,
  [ProjectCode.AQUA]: aquaColorTheme,
  [ProjectCode.PP]: ppColorTheme,
  [ProjectCode.PM]: pmColorTheme,
  [ProjectCode.YT]: ytColorTheme,
}

export function CampaignRouters() {
  const location = useLocation()
  const history = useHistory()

  const { campaignCodeFromUrl } = useCampaignCode()

  const { destructuredData: campaign } = useApiCampaignActivityInfo(campaignCodeFromUrl)
  const campaignId = campaign?.campaign_id ?? undefined
  const { region: countryCode } = campaign || {}
  const { destructuredData: exhibitorSettings, isLoading: isActivityLoading, error } = useApiCampaignActivityInfo(campaignCodeFromUrl)
  const { isProductsFetching, projectCode, setDefaultProjectCode } = useFpCampaignProductsService(campaignCodeFromUrl)
  const memberLoginType = exhibitorSettings?.member_login_type
  const isRequiredLogin = memberLoginType === MemberLogins.REQUIRED
  const isSuggestLogin = memberLoginType === MemberLogins.SUGGEST
  const canLogin = isSuggestLogin || isRequiredLogin
  const { destructuredData: campaignMember, isLoading: isCampaignMemberLoading } = useApiCampaignMember(canLogin ? { code: campaignCodeFromUrl } : undefined, {
    shouldRetryOnError: (error) => {
      if (error?.response?.status === 403 && error?.response?.data?.error?.code === FORTUNE_GROUP_BUYING_AUTH_ERROR) {
        return false
      }
      if (error?.response?.data.error?.code === THIRD_PARTY_ORDER_ERROR_CODES.INVALID_PARAMETERS) {
        return false
      }
      if (error?.response?.data.error?.type === 'AUTHENTICATION_ERROR') {
        return false
      }
      return true
    },
  })

  const isMultiBrand = campaign?.brand_codes_info?.length && campaign?.brand_codes_info?.length > 1

  const colorTheme = isMultiBrand ? fastpassColorTheme : colorThemeMap[projectCode || ProjectCode.DCS] || dcsColorTheme

  useEffectOnce(() => {
    document.title = 'Buygather'
  })

  const { changeLanguage } = useLanguage()

  useEffect(() => {
    const language = exhibitorSettings?.language as LocaleMapKey ?? 'zh-TW'
    changeLanguage(language)
  }, [changeLanguage, exhibitorSettings?.language])

  useEffect(() => {
    setDefaultProjectCode()
  }, [setDefaultProjectCode])

  const { setExpiredTimestamp } = storageGuidedLogin({
    key: GUIDED_LOGIN_KEY.THANK_YOU_PAGE,
    campaignCode: campaignCodeFromUrl,
  })

  const handleLogout = () => {
    setExpiredTimestamp(new Date().getTime())
    history.push(`/campaigns/${campaignCodeFromUrl}/logout`)
  }

  const handleToCart = () => {
    history.push(`/campaigns/${campaignCodeFromUrl}/cart`)
  }

  const isNotFoundCampaign = error?.status === 404
  if (isNotFoundCampaign) {
    return (
      <NotFound />
    )
  }

  if (isProductsFetching || isActivityLoading || !projectCode) {
    return (
      <LoaderBuygather fontSize={60} />
    )
  }

  /**
   * 回傳不在展期內的頁面設定
   */
  const isCampaignClosed = exhibitorSettings?.status && [CampaignStatus.EXPIRED, CampaignStatus.NOT_YET, CampaignStatus.PAUSED].includes(exhibitorSettings.status)
  if (isCampaignClosed && !location.pathname.includes('thankyou')) {
    return (
      <AppBarProvider onLogout={handleLogout} environment="campaign">
        <RefreshCosignToken />
        <ProductCatalogProvider
          countryCode={countryCode as CountryCode}
          campaignId={campaignId as number}
          campaignCode={campaignCodeFromUrl}
          projectCode={projectCode}
        >
          <Suspense>
            <PopupWrapper projectCode={projectCode} campaignId={campaignId as number} countryCode={countryCode as CountryCode} />
          </Suspense>
          <Switch>
            <Route path="/campaigns/:campaignID" exact><ClientClosed /></Route>
            <Route path="/campaigns/:campaignID/FAQ"><ClientFAQ /></Route>
            <Route path="/campaigns/:campaignID/privacy"><ClientPrivacyPolicy /></Route>
            <Route path="/campaigns/:campaignID/login" exact><CampaignLogin /></Route>
            <Route path="/campaigns/:campaignID/auth-processing" exact><CampaignAuthProcessingWithWordPress /></Route>
            <Route path="/campaigns/:campaignID/logout">{campaignId && <Logout campaignId={campaignId} environment="campaign" />}</Route>
            <Route path={['/campaigns/:campaignID/*', '/*']}><ClientClosed shouldHideHeader /></Route>
          </Switch>
        </ProductCatalogProvider>
      </AppBarProvider>
    )
  }

  if (isRequiredLogin && !campaignMember && !isCampaignMemberLoading) {
    return (
      <AppBarProvider onLogout={handleLogout} environment="campaign">
        <RefreshCosignToken />
        <ProductCatalogProvider
          countryCode={countryCode as CountryCode}
          campaignId={campaignId as number}
          campaignCode={campaignCodeFromUrl}
          projectCode={projectCode}
        >
          <Suspense>
            <PopupWrapper projectCode={projectCode} campaignId={campaignId as number} countryCode={countryCode as CountryCode} />
          </Suspense>
          <Switch>
            <Route path="/campaigns/:campaignID/login" exact><CampaignLogin /></Route>
            <Route path="/campaigns/:campaignID/thankyou/:orderHash"><ClientThankyou /></Route>
            <Route path="/campaigns/:campaignID/auth-processing" exact><CampaignAuthProcessing /></Route>
            <Route path={['/campaigns/:campaignID/*', '/campaigns/:campaignID/']}><Redirect to={`/campaigns/${campaignCodeFromUrl}/login`} /></Route>
          </Switch>
        </ProductCatalogProvider>
      </AppBarProvider>
    )
  }

  return (
    <ThemeProvider theme={colorTheme}>
      <RefreshCosignToken />
      <AppBarProvider toCartPage={handleToCart} environment="campaign" onLogout={handleLogout}>
        <ProductCatalogProvider
          projectCode={projectCode}
          countryCode={countryCode as CountryCode}
          campaignId={campaignId as number}
          campaignCode={campaignCodeFromUrl}
          currency={campaign?.currency}
        >
          <CampaignCartProvider
            projectCode={projectCode}
            countryCode={countryCode as CountryCode}
            campaignId={campaignId as number}
            campaignCode={campaignCodeFromUrl}
            currency={campaign?.currency}
            fallbackUrl={`/campaigns/${campaignCodeFromUrl}/`}
            discardAllRedirectUrl={`/campaigns/${campaignCodeFromUrl}/`}
            checkoutUrl={`/campaigns/${campaignCodeFromUrl}/checkout`}
          >
            <Suspense>
              <PopupWrapper projectCode={projectCode} campaignId={campaignId as number} countryCode={countryCode as CountryCode} />
            </Suspense>
            <Switch>
              <Route path={['/', '/campaigns/:campaignID']} exact>
                <TheCampaignHeader projectCode={projectCode} countryCode={countryCode as CountryCode} campaignId={campaignId as number} />
                <ProductCatalog />
              </Route>
              <Route path="/campaigns/:campaignID/FAQ"><ClientFAQ /></Route>
              <Route path="/campaigns/:campaignID/privacy"><ClientPrivacyPolicy /></Route>
              <Route path="/campaigns/:campaignID/contact"><ClientContact /></Route>
              <Route path="/campaigns/:campaignID/cart" exact>
                <TheCampaignHeader projectCode={projectCode} countryCode={countryCode as CountryCode} campaignId={campaignId as number} />
                <Cart />
              </Route>
              {campaignMember && (
                <Route path="/campaigns/:campaignID/my-account/order" exact>
                  {countryCode && campaignId && campaign?.site_id && campaignMember
                  && <CampaignOrderList projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} siteId={campaign.site_id} />}
                </Route>
              )}
              <Route path="/campaigns/:campaignID/my-account/order-detail/:transactionId">
                <OrderDetailContainer countryCode={countryCode as CountryCode} />
              </Route>
              <Route path="/campaigns/:campaignID/checkout"><ClientCheckout /></Route>
              <Route path="/campaigns/:campaignID/payments"><ClientPayments /></Route>
              <Route path="/campaigns/:campaignID/thankyou/:orderHash"><ClientThankyou /></Route>
              <Route path="/campaigns/:campaignID/logout">{campaignId && <Logout campaignId={campaignId} environment="campaign" />}</Route>
              <Route path="/campaigns/:campaignID/admin-login"><Redirect to={`/admin/${campaignCodeFromUrl}/admin-login`} /></Route>
              {canLogin && projectCode && !campaignMember && <Route path="/campaigns/:campaignID/login" exact><CampaignLogin /></Route>}
              {canLogin && projectCode && !campaignMember && <Route path="/campaigns/:campaignID/auth-processing" exact><CampaignAuthProcessing /></Route>}
              <Route path="/campaigns/:campaignID/*"><NotFound /></Route>
            </Switch>
          </CampaignCartProvider>
        </ProductCatalogProvider>
      </AppBarProvider>
      <DialogValidateStock />
    </ThemeProvider>
  )
}
